import { useState, useRef, useEffect } from 'react';
import {
  Paper,
  Box,
  Typography,
  IconButton,
  Button,
  useMediaQuery,
  Chip,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useAccount } from '../../contexts/Account';
import eventBus, { eventBusValues } from '../../eventBus';
import { getSavedCompanies } from '../../services/advisor_services';
import BoardCard from '../BoardCard';
import { getAdvisorQueryParams } from '../../utils/queryParams';

function InvitationsBoard({ setHideComponent = () => {} }) {
  const { accountDetails, isFreeTrial, isFreemium } = useAccount();
  const [savedCompanies, setSavedCompanies] = useState([]);
  const [hideScroll, setHideScroll] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [width, setWidth] = useState(0);

  const scrollContainerRef = useRef(null);
  const containerRef = useRef(null);
  const isXs = useMediaQuery((theme) => theme.breakpoints.down('xs'));

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const newWidth = containerRef.current.offsetWidth;
        const viewportWidth = window.innerWidth - 80;
        setWidth(Math.min(newWidth, viewportWidth));
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    loadSavedCompanies();
    eventBus.on(eventBusValues.getSavedCompaniesData, loadSavedCompanies);
    return () => {
      eventBus.remove(eventBusValues.getSavedCompaniesData, loadSavedCompanies);
    };
  }, []);

  const loadSavedCompanies = () => {
    getSavedCompanies({ advisorId: accountDetails.id })
      .then((response) => {
        const companies = response.data.getSavedCompanies.filter(
          (c) =>
            c.boardOwnerStatus !== 'inactive' &&
            c.boardOwnerStatus !== 'paused' &&
            c.opportunityStage !== 'dormant' &&
            c.opportunityStage !== 'pending_cancellation' &&
            c.opportunityStage !== 'position_filled'
        );
        setSavedCompanies(companies);
        setHideComponent(
          companies.length === 0 &&
            accountDetails.boards.invitedBoards.length === 0
        );
      })
      .catch((error) =>
        console.error('Error fetching saved companies:', error)
      );
  };

  useEffect(() => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const { scrollWidth } = container;
      if (scrollWidth <= width) {
        setHideScroll(true);
      } else {
        setHideScroll(false);
      }
    }
  }, [width, savedCompanies]);

  const allInvitations = accountDetails.boards.invitedBoards
    .filter(
      (b) =>
        b.opportunityStage !== 'position_filled' &&
        b.opportunityStage !== 'dormant' &&
        b.opportunityStage !== 'pending_cancellation' &&
        b.boardStatus !== 'inactive' &&
        b.boardStatus !== 'paused'
    )
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const totalInvitations = allInvitations.length;
  const totalSaved = savedCompanies.length;

  let displayedInvitations = allInvitations;
  let displayedSaved = savedCompanies;

  // On xs screens, limit to 1 of each when both types exist, else up to 2 of whichever exists
  if (isXs && !expanded) {
    if (totalInvitations > 0 && totalSaved > 0) {
      displayedInvitations = allInvitations.slice(0, 1);
      displayedSaved = savedCompanies.slice(0, 1);
    } else if (totalInvitations > 2) {
      displayedInvitations = allInvitations.slice(0, 2);
      displayedSaved = [];
    } else if (totalSaved > 2) {
      displayedSaved = savedCompanies.slice(0, 2);
      displayedInvitations = [];
    }
  }

  const showMoreButton =
    isXs && totalInvitations + totalSaved > (expanded ? 0 : 2);

  const scrollRight = () => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const { scrollLeft, scrollWidth, clientWidth, scrollRight } = container;
    const jump = 300;

    if (scrollLeft + clientWidth + 50 >= scrollWidth) {
      container.scrollTo({
        left: 0,
        behavior: 'smooth',
      });
    } else {
      container.scrollBy({
        left: jump,
        behavior: 'smooth',
      });
    }
  };

  const scrollLeft = () => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const jump = 300;
    if (container.scrollLeft === 0) {
      container.scrollTo({ left: container.scrollWidth, behavior: 'smooth' });
    } else {
      container.scrollBy({ left: -jump, behavior: 'smooth' });
    }
  };

  function handleButtonClick({ company, companyId }) {
    eventBus.dispatch(eventBusValues.triggerCompanyDrawerAction, {
      company: company || null,
      companyId: companyId || null,
    });
  }

  function handleGetMatchedClick() {
    // no current action
    // if (isFreemium) {
    //   eventBus.dispatch(eventBusValues.triggerAdvisorUpgradeAction, {
    //     origin: 'directory-button',
    //   });
    // } else {
    //   window.location.replace(
    //     `https://register.advisorycloud.com/get-assistance-boards?${getAdvisorQueryParams(
    //       {
    //         accountDetails,
    //         isFreeTrial,
    //         isFreemium,
    //       }
    //     )}`
    //   );
    // }
  }

  return (
    <Paper
      id="invitations-tile"
      ref={containerRef}
      elevation={1}
      sx={{
        padding: '15px',
        borderRadius: '12px',
        marginBottom: '20px',
        boxShadow: 'none',
        maxWidth: '100%',
        position: 'relative',
      }}
    >
      <Typography
        sx={{ fontWeight: 600, fontSize: 16, mb: '4px', color: '#001030' }}
      >
        Board Matches & Opportunities
      </Typography>
      <Typography
        sx={{
          fontWeight: 400,
          fontSize: 14,
          color: '#4D586E',
          fontStyle: 'italic',
          mb: '16px',
        }}
      >
        Companies seek diverse opinions and may be outside your usual industry.
        You can join or leave boards at any time to see which ones are a fit.
      </Typography>

      {!hideScroll && (
        <IconButton
          onClick={scrollLeft}
          sx={{
            display: { xxs: 'none', xs: 'flex' },
            position: 'absolute',
            borderRadius: '8px',
            left: 15,
            bottom: 45,
            height: 20,
            width: 20,
            transform: 'translateY(-600%)',
            zIndex: 10,
            color: '#000',
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
      )}

      <Box
        ref={scrollContainerRef}
        sx={{
          display: { xxs: 'block', xs: 'flex' },
          overflowX: { xxs: 'visible', xs: 'auto' },
          overflowY: 'visible',
          whiteSpace: { xxs: 'normal', xs: 'nowrap' },
          scrollBehavior: 'smooth',
          m: { xxs: 0, xs: hideScroll ? 0 : '0 40px' },
          maxWidth: {
            xxs: '100%',
            xs: width - 40,
            md: width + 200,
            lg: width + 500,
          },
        }}
      >
        {displayedSaved.map((comp, idx) => (
          <Box
            key={idx}
            className="topMatch"
            sx={{
              marginBottom: '20px',
              paddingTop: '10px',
              marginRight: { xxs: '0px', xs: '16px' },
              width: { xxs: '100%', sm: '350px' },
            }}
          >
            <BoardCard
              board={{
                ...comp,
                companyLogo: comp.image?.location
                  ? comp.image.location.split('https://')[1]
                  : null,
              }}
              hideMemberSince
              companyList
              width={isXs ? '100%' : '350px'}
              onClick={() => handleButtonClick({ company: comp })}
              displayPillsFlag
              Chip={() => (
                <Chip
                  label="Top Match"
                  size="small"
                  sx={{
                    position: 'absolute',
                    fontSize: '12px',
                    height: '20px',
                    top: -10,
                    right: { xxs: -12, xs: -10 },
                    zIndex: 10,
                    backgroundColor: '#52b5f1',
                    color: '#fff',
                    fontWeight: 500,
                    padding: '13px 25px',
                  }}
                />
              )}
            />
          </Box>
        ))}

        {displayedInvitations.map((inv, idx) => (
          <Box
            key={idx}
            className="ITJ"
            sx={{
              marginBottom: '20px',
              paddingTop: '10px',
              marginRight: { xxs: '0px', xs: '16px' },
              width: { xxs: '100%', sm: '350px' },
            }}
          >
            <BoardCard
              board={inv}
              hideMemberSince
              companyList
              width={isXs ? '100%' : '350px'}
              onClick={() => handleButtonClick({ companyId: inv.id })}
              displayPillsFlag
              Chip={() => (
                <Chip
                  label="Invitation"
                  size="small"
                  sx={{
                    position: 'absolute',
                    fontSize: '12px',
                    height: '20px',
                    top: -10,
                    right: { xxs: -12, xs: -10 },
                    zIndex: 10,
                    backgroundColor: '#3670f8',
                    color: '#fff',
                    fontWeight: 500,
                    padding: '13px 25px',
                  }}
                />
              )}
            />
          </Box>
        ))}

        {/* Static tiles */}
        <Box
          className="getMatched"
          sx={{
            marginBottom: '20px',
            paddingTop: '10px',
            marginRight: { xxs: '0px', xs: '16px' },
            width: { xxs: '100%', sm: '350px' },
          }}
        >
          <BoardCard
            board={{
              companyName: 'Get Matched',
              headline:
                'Let our team match you to the right boards in minutes based on your professional experience, interests and unique expertise.',
            }}
            buttonText="Get Matched"
            buttonId="get-matched-button"
            hideMemberSince
            companyList
            goalImage
            staticTile
            width={isXs ? '100%' : '350px'}
            onClick={handleGetMatchedClick}
            Chip={() => (
              <Chip
                label="Complimentary"
                size="small"
                sx={{
                  position: 'absolute',
                  fontSize: '12px',
                  height: '20px',
                  top: -10,
                  right: { xxs: -12, xs: -10 },
                  zIndex: 10,
                  backgroundColor: '#2B71F6',
                  color: '#fff',
                  fontWeight: 500,
                  padding: '13px 14px',
                }}
              />
            )}
          />
        </Box>
        <Box
          className="viewAllBoards"
          sx={{
            marginBottom: '20px',
            paddingTop: '10px',
            marginRight: { xxs: '0px', xs: '16px' },
            width: { xxs: '100%', sm: '350px' },
          }}
        >
          <BoardCard
            board={{
              companyName: 'View All Boards',
              headline:
                'Explore our exclusive directory of 100+ advisory boards seeking outside perspective and expertise like yours.',
            }}
            buttonText="View All"
            hideMemberSince
            companyList
            staticTile
            width={isXs ? '100%' : '350px'}
          />
        </Box>
      </Box>

      {!hideScroll && (
        <IconButton
          onClick={scrollRight}
          sx={{
            display: { xxs: 'none', xs: 'flex' },
            position: 'absolute',
            borderRadius: '8px',
            right: 15,
            bottom: 45,
            height: 20,
            width: 20,
            transform: 'translateY(-600%)',
            zIndex: 10,
            color: '#000',
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      )}

      {showMoreButton && (
        <Box sx={{ textAlign: 'center', mt: '0px' }}>
          <Button
            variant="text"
            onClick={() => setExpanded(!expanded)}
            sx={{
              border: '1px solid #2C71F64D',
              borderRadius: '8px',
              color: '#2C71F6',
              backgroundColor: '#fff',
              p: '4px 16px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              width: '100%',
              height: '40px',
              '&:hover': { backgroundColor: '#F3F4F6' },
            }}
          >
            {expanded ? 'Less' : 'More'}
          </Button>
        </Box>
      )}
    </Paper>
  );
}

export default InvitationsBoard;
